import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { bulkUploadStudentsCSV, bulkUploadStaffCSV, downloadResultCSV } from "services/api-service";

export default function StudentDetails() {
  const history = useHistory();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkType, setBulkType] = useState(null); // "member" or "staff"
  const [csvFile, setCsvFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  // Download sample CSV template
  const downloadSampleCSV = (type) => {
    let csvContent = '';
    let filename = '';

    if (type === 'member') {
      csvContent = `Student_id,first_name,last_name,email,mobile_no,gender,gym_location,membership_start_date,membership_end_date\nM001,Deepanshu,Bhinda,deepanshu.bhinda@example.com,9876543210,Male,location1,2026-02-23,2026-04-03\nM002,Ananya,Singh,ananya.singh@example.com,9876543211,Female,location2,2026-03-01,2026-05-01\nM003,Vikram,Patel,vikram.patel@example.com,9876543212,Male,location3,2026-02-15,2026-06-15`;
      filename = 'sample_members_template.csv';
    } else {
      csvContent = `staff_id,first_name,last_name,email,mobile_no,department\nST001,Ramesh,Sharma,ramesh.sharma@example.com,9876543220,Mathematics\nST002,Priya,Desai,priya.desai@example.com,9876543221,English\nST003,Ajay,Nair,ajay.nair@example.com,9876543222,Science`;
      filename = 'sample_staff_template.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkUpload = async () => {
    if (!csvFile) {
      setUploadStatus("error:Please select a file");
      return;
    }

    try {
      setUploadStatus(`info:Uploading ${bulkType} data...`);
      setUploadProgress(30);

      let response;
      if (bulkType === 'member') {
        response = await bulkUploadStudentsCSV(csvFile);
      } else {
        response = await bulkUploadStaffCSV(csvFile);
      }

      setUploadProgress(70);
      downloadResultCSV(response.data, `${bulkType}_upload_result_${new Date().getTime()}.csv`);
      setUploadProgress(100);
      setUploadStatus(`success:Successfully uploaded ${bulkType} data!`);
      setCsvFile(null);
      
      setTimeout(() => {
        setShowBulkModal(false);
        setBulkType(null);
        setUploadStatus("");
        setUploadProgress(0);
      }, 2500);

    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      setUploadStatus(`error:${errorMsg}`);
      setUploadProgress(0);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        setUploadStatus("error:Please upload a valid CSV file");
        setCsvFile(null);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setUploadStatus("error:File size must be less than 10MB");
        setCsvFile(null);
        return;
      }
      setCsvFile(file);
      setUploadStatus("");
    }
  };

  // Status message parser
  const getStatusConfig = () => {
    if (!uploadStatus) return null;
    const [type, msg] = uploadStatus.split(':');
    if (type === 'success') return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', icon: '✓', msg };
    if (type === 'error') return { bg: '#fee2e2', color: '#991b1b', border: '#fecaca', icon: '⚠', msg };
    return { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe', icon: 'ℹ', msg };
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="pro-container">
      {/* --- CSS STYLES --- */}
      <style>{`
        .pro-container { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #1e293b; padding: 24px; }
        .control-panel { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05); }
        .panel-header { margin-bottom: 24px; }
        .panel-title { font-size: 1.25rem; font-weight: 600; color: #0f172a; margin: 0 0 4px 0; }
        .panel-subtitle { font-size: 0.875rem; color: #64748b; margin: 0; }
        
        .btn-group { display: flex; gap: 12px; flex-wrap: wrap; }
        .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 20px; font-size: 0.875rem; font-weight: 500; border-radius: 8px; cursor: pointer; transition: all 0.2s ease; border: 1px solid transparent; }
        
        .btn-primary { background: #4f46e5; color: white; }
        .btn-primary:hover { background: #4338ca; box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25); transform: translateY(-1px); }
        .btn-secondary { background: #ffffff; border-color: #cbd5e1; color: #334155; }
        .btn-secondary:hover { background: #f8fafc; border-color: #94a3b8; }
        .btn-danger { background: #ef4444; color: white; }
        .btn-danger:hover { background: #dc2626; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25); transform: translateY(-1px); }
        
        .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 9999; animation: fadeIn 0.2s ease-out; padding: 20px; }
        .modal-content { background: white; border-radius: 16px; width: 100%; max-width: 500px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); overflow: hidden; }
        .modal-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
        .modal-header h3 { margin: 0; font-size: 1.125rem; font-weight: 600; color: #0f172a; }
        .close-btn { background: transparent; border: none; color: #94a3b8; cursor: pointer; border-radius: 6px; padding: 4px; display: flex; align-items: center; transition: 0.2s; }
        .close-btn:hover { background: #f1f5f9; color: #0f172a; }
        
        .modal-body { padding: 24px; }
        
        .selection-grid { display: grid; gap: 16px; }
        .select-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; text-align: left; cursor: pointer; transition: all 0.2s ease; display: flex; flex-direction: column; gap: 8px; background: #ffffff; }
        .select-card:hover { border-color: #4f46e5; box-shadow: 0 4px 12px rgba(79, 70, 229, 0.1); transform: translateY(-2px); }
        .select-card.danger:hover { border-color: #ef4444; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.1); }
        .select-card-title { font-weight: 600; font-size: 1rem; color: #1e293b; display: flex; align-items: center; gap: 8px; }
        .select-card-desc { font-size: 0.8125rem; color: #64748b; line-height: 1.5; margin: 0; }
        
        .upload-zone { border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px 24px; text-align: center; cursor: pointer; background: #f8fafc; transition: all 0.2s ease; margin-bottom: 20px; }
        .upload-zone:hover { border-color: #6366f1; background: #e0e7ff; }
        .upload-zone.has-file { border-color: #10b981; background: #ecfdf5; border-style: solid; }
        
        .progress-bar-bg { width: 100%; height: 6px; background: #e2e8f0; border-radius: 999px; overflow: hidden; margin-bottom: 24px; }
        .progress-bar-fill { height: 100%; background: #4f46e5; transition: width 0.3s ease; }
        
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>

      {/* --- DASHBOARD CONTROL PANEL --- */}
      <div className="control-panel">
        <div className="panel-header">
          <h2 className="panel-title">Directory Management</h2>
          <p className="panel-subtitle">Manage members, staff, and external alerts.</p>
        </div>
        
        <div className="btn-group">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
            Add Record
          </button>
          
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit Details
          </button>
          
          <button className="btn btn-secondary" onClick={() => setShowBulkModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Bulk Upload
          </button>
          
          <button className="btn btn-danger" onClick={() => history.push("/admin/addSuspect")} style={{marginLeft: 'auto'}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Add Suspect
          </button>
        </div>
      </div>

      {/* --- MODAL 1: ADD RECORD --- */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Profile</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body selection-grid">
              <div className="select-card" onClick={() => { history.push("/admin/addMember"); setShowAddModal(false); }}>
                <div className="select-card-title">
                  <span style={{background: '#e0e7ff', color: '#4f46e5', padding: '6px', borderRadius: '8px'}}>👨‍🎓</span> Add Customer Profile
                </div>
                <p className="select-card-desc">Register a new Customer, assign Buying Pattern, and capture facial data.</p>
              </div>
              <div className="select-card" onClick={() => { history.push("/admin/addstaff"); setShowAddModal(false); }}>
                <div className="select-card-title">
                  <span style={{background: '#fce7f3', color: '#be185d', padding: '6px', borderRadius: '8px'}}>👨‍🏫</span> Add Staff Profile
                </div>
                <p className="select-card-desc">Onboard new faculty or administrative staff into the system.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 2: EDIT RECORD --- */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modify Existing Records</h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body selection-grid">
              <div className="select-card" onClick={() => { history.push("/admin/editmember"); setShowEditModal(false); }}>
                <div className="select-card-title">✏️ Edit Customer</div>
                <p className="select-card-desc">Update Customer details, credentials, or re-capture Customer's data.</p>
              </div>
              <div className="select-card" onClick={() => { history.push("/admin/editstaff"); setShowEditModal(false); }}>
                <div className="select-card-title">✏️ Edit Staff</div>
                <p className="select-card-desc">Modify staff department roles, schedules, or personal information.</p>
              </div>
              <div className="select-card danger" onClick={() => { history.push("/admin/suspect-list"); setShowEditModal(false); }}>
                <div className="select-card-title" style={{color: '#dc2626'}}>🚨 Manage Suspects</div>
                <p className="select-card-desc">Review, edit, or remove flagged individuals from the security watch list.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 3: BULK UPLOAD - TYPE SELECTION --- */}
      {showBulkModal && !bulkType && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Ingestion</h3>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body selection-grid">
              <div className="select-card" style={{borderColor: '#bfdbfe', background: '#f8fafc'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>

                </div>
              </div>

              <div className="select-card" style={{borderColor: '#fbcfe8', background: '#fdf2f8'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                  <div>
                    <div className="select-card-title">Staff Data Ingestion</div>
                    <p className="select-card-desc" style={{marginTop:'8px', marginBottom:'16px'}}>Upload a structured CSV for bulk staff onboarding.</p>
                  </div>
                  <button className="btn btn-secondary" style={{padding:'6px 12px', fontSize:'12px', borderColor: '#f472b6', color: '#be185d'}} onClick={(e) => { e.stopPropagation(); downloadSampleCSV('staff'); }}>
                    📥 Template
                  </button>
                </div>
                <button className="btn btn-danger" style={{width:'100%', background: '#db2777'}} onClick={() => setBulkType('staff')}>Upload Staff</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 4: BULK UPLOAD - FILE DROP --- */}
      {showBulkModal && bulkType && (
        <div className="modal-overlay" onClick={() => { setShowBulkModal(false); setBulkType(null); setCsvFile(null); setUploadStatus(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                <button className="close-btn" style={{padding:0}} onClick={() => { setBulkType(null); setCsvFile(null); setUploadStatus(''); setUploadProgress(0); }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                </button>
                <h3>Upload {bulkType === 'member' ? 'Students' : 'Staff'} CSV</h3>
              </div>
              <button className="close-btn" onClick={() => { setShowBulkModal(false); setBulkType(null); setCsvFile(null); setUploadStatus(''); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            
            <div className="modal-body">
              <div className={`upload-zone ${csvFile ? 'has-file' : ''}`}>
                <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} id="csvInput" />
                <label htmlFor="csvInput" style={{ cursor: 'pointer', display: 'block', margin: 0 }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={csvFile ? "#10b981" : "#64748b"} strokeWidth="1.5" style={{marginBottom:'12px'}}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <div style={{ fontSize: '1rem', color: '#1e293b', fontWeight: '600', marginBottom: '8px' }}>
                    {csvFile ? 'File Selected' : 'Click to browse files'}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                    {csvFile ? <span style={{ color: '#10b981' }}>{csvFile.name}</span> : 'Limit 10MB • CSV strictly'}
                  </div>
                </label>
              </div>

              {statusConfig && (
                <div style={{ padding: '12px 16px', borderRadius: '8px', fontSize: '13px', display:'flex', gap:'8px', alignItems:'center', marginBottom: '20px', backgroundColor: statusConfig.bg, color: statusConfig.color, border: `1px solid ${statusConfig.border}` }}>
                  <span style={{fontWeight:'bold'}}>{statusConfig.icon}</span>
                  {statusConfig.msg}
                </div>
              )}

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div>
                  <div style={{display:'flex', justifyContent:'space-between', fontSize:'12px', color:'#64748b', marginBottom:'6px'}}>
                    <span>Uploading data...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="progress-bar-bg">
                    <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setShowBulkModal(false); setBulkType(null); setCsvFile(null); setUploadStatus(''); setUploadProgress(0); }}>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 1, opacity: (!csvFile || (uploadProgress > 0 && uploadProgress < 100)) ? 0.5 : 1 }}
                  disabled={!csvFile || (uploadProgress > 0 && uploadProgress < 100)}
                  onClick={handleBulkUpload}
                >
                  {uploadProgress > 0 && uploadProgress < 100 ? 'Processing...' : 'Start Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}